import { useMemo } from 'react'
import { useMutation, useQuery } from 'react-query'
import useGroupsQueries from '../api/groupsQueries'
import { Group, GroupDeletePayload, GroupUpdatePayload } from '../types'
import { UserRole } from 'features/users'
import { CartoApiError } from 'features/common'

interface UseGroupsProps {
  search?: string
  roleFilter?: UserRole
}

export function useGroups({ search, roleFilter }: UseGroupsProps = {}) {
  const { queries } = useGroupsQueries()

  const { data, ...queryResult } = useQuery({ ...queries.getGroups })

  const filteredGroups = useMemo(() => {
    let groups = data || []

    if (search) {
      const termToSearch = search.toLowerCase()
      groups = groups.filter(
        (group) =>
          group.customName.toLowerCase().includes(termToSearch) ||
          group.externalName.toLowerCase().includes(termToSearch)
      )
    }

    if (roleFilter) {
      groups = groups.filter((group) => group.roleMapping === roleFilter)
    }

    return groups
  }, [data, search, roleFilter])

  return {
    ...queryResult,
    data: filteredGroups
  }
}

interface UseGroupMembersProps {
  search?: string
  roleFilter?: UserRole
  groupId: string
}

export function useGroupMembers({ search, roleFilter, groupId }: UseGroupMembersProps) {
  const { queries } = useGroupsQueries()

  const { data, ...queryResult } = useQuery(queries.getGroupMembers(groupId))

  const filteredGroupMembers = useMemo(() => {
    let members = data || []
    if (search) {
      const termToSearch = search.toLowerCase()
      members = members.filter((member) => member.email.toLowerCase().includes(termToSearch))
    }

    if (roleFilter) {
      members = members.filter((member) => member.role === roleFilter)
    }

    return members
  }, [data, search, roleFilter])

  return {
    ...queryResult,
    data: filteredGroupMembers
  }
}

export function useGroupsByIds(ids?: string[]) {
  const { data: allGroups, ...other } = useGroups()
  const groups = useMemo(
    () =>
      ids
        ?.map((groupId) => allGroups.find((g) => g.id === groupId))
        .filter((g: Group | undefined) => g !== undefined) as Group[],
    [allGroups, ids]
  )
  return {
    data: groups,
    ...other
  }
}

export function useUpdateGroup() {
  const { queries } = useGroupsQueries()

  return useMutation<Group, { error: string; status: number }, GroupUpdatePayload>(queries.updateGroup)
}

export function useDeleteGroup() {
  const { queries } = useGroupsQueries()

  return useMutation<Group, CartoApiError, GroupDeletePayload>(queries.deleteGroup)
}

export function useCreateGroup() {
  const { queries } = useGroupsQueries()

  return useMutation<Group, Record<string, unknown>, Partial<Group>>(queries.createGroup)
}
